import { useEffect, useState } from "react";

import { avatarColorFor } from "./avatarColor";
import { User } from "./types";

export type ResolvedUser = Pick<
  User,
  "email" | "first_name" | "last_name" | "avatar"
>;

type AvatarSize = "sm" | "md";

interface AvatarProps {
  user?: ResolvedUser;
  fallbackLabel?: string;
  size?: AvatarSize;
}

const SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: "h-6 min-w-6 text-xs",
  md: "h-9 min-w-9",
};

export function initialsForUser(user: ResolvedUser): string {
  const firstInitial = user.first_name?.trim()?.[0] ?? "";
  const lastInitial = user.last_name?.trim()?.[0] ?? "";
  const nameInitials = (firstInitial + lastInitial).toUpperCase();
  if (nameInitials) {
    return nameInitials;
  }

  return (user.email ?? "").slice(0, 2).toUpperCase();
}

// Initials for a raw display string (a name, an email, or "Freshpaint") that
// has no resolvable user behind it.
function initialsForLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) {
    return "?";
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  return trimmed.slice(0, 2).toUpperCase();
}

export function Avatar({ user, fallbackLabel, size = "md" }: AvatarProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const sizeClass = SIZE_CLASSES[size];

  useEffect(() => {
    setImageFailed(false);
  }, [user?.avatar, user?.email, fallbackLabel]);

  if (user?.avatar && !imageFailed) {
    return (
      <img
        src={user.avatar}
        alt=""
        aria-hidden
        className={`${sizeClass} rounded-full object-cover`}
        onError={() => setImageFailed(true)}
      />
    );
  }

  const initials = user
    ? initialsForUser(user)
    : fallbackLabel
      ? initialsForLabel(fallbackLabel)
      : "?";
  const colorKey = user ? (user.email ?? "") : (fallbackLabel ?? "");

  return (
    <span
      className={`flex ${sizeClass} items-center justify-center rounded-full ${avatarColorFor(colorKey)} text-white uppercase`}
    >
      {initials || "FP"}
    </span>
  );
}
